import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { calculateForumScore } from '../utils/forumScore';
import { usePageSeo } from '../hooks/usePageSeo';
import { DEFAULT_FORUM_LOGO_URL } from '../types/forum';

/** Minimal iframe widget — /embed/:forumId */
const EmbedForum: React.FC = () => {
  const { forumId } = useParams<{ forumId: string }>();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'light';
  const accent = searchParams.get('accent') || '#4f46e5';
  const label = searchParams.get('label') || 'kitapcik.net';
  const compact = searchParams.get('compact') === '1';
  const dark = theme === 'dark';
  const [data, setData] = useState<{
    forum_id: string;
    name: string;
    category: string;
    logo_url: string | null;
    members: number;
    topics: number;
    posts: number;
    score: number;
    globalRank: number | null;
    categoryRank: number | null;
  } | null>(null);

  usePageSeo({
    title: 'Forum widget',
    description: 'Forum istatistik embed',
    keywords: '',
    noindex: true,
  });

  useEffect(() => {
    document.documentElement.style.margin = '0';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!forumId) return;
    (async () => {
      const { data: row } = await supabase
        .from('forums')
        .select('id, forum_id, name, category, logo_url, members_count, topics_count, posts_count, founded_year')
        .eq('forum_id', forumId)
        .eq('is_active', true)
        .maybeSingle();

      if (!row) return;

      const myScore = calculateForumScore(
        row.topics_count,
        row.posts_count,
        row.members_count,
        row.founded_year
      );

      // Ranking için minimal liste (aktif forumlar).
      const { data: all } = await supabase
        .from('forums')
        .select('id, category, members_count, topics_count, posts_count, founded_year')
        .eq('is_active', true);

      const list = (all || []).map((f) => ({
        id: f.id as string,
        category: (f.category as string) || '—',
        score: calculateForumScore(
          (f.topics_count as number) ?? 0,
          (f.posts_count as number) ?? 0,
          (f.members_count as number) ?? 0,
          (f.founded_year as number) ?? 1
        ),
      }));

      const globalSorted = [...list].sort((a, b) => b.score - a.score);
      const globalRank = globalSorted.findIndex((x) => x.id === row.id) + 1;

      const catSorted = globalSorted.filter((x) => x.category === row.category);
      const categoryRank = catSorted.findIndex((x) => x.id === row.id) + 1;

      setData({
        forum_id: row.forum_id,
        name: row.name,
        category: row.category || '—',
        logo_url: row.logo_url || null,
        members: row.members_count,
        topics: row.topics_count,
        posts: row.posts_count,
        score: myScore,
        globalRank: globalRank > 0 ? globalRank : null,
        categoryRank: categoryRank > 0 ? categoryRank : null,
      });
    })();
  }, [forumId]);

  if (!data) {
    return (
      <div style={{ fontFamily: 'system-ui', padding: 16, fontSize: 14, color: '#666' }}>
        Yükleniyor…
      </div>
    );
  }

  const bg = dark ? '#0b1220' : '#ffffff';
  const border = dark ? '#243043' : '#e5e7eb';
  const text = dark ? '#e5e7eb' : '#0f172a';
  const muted = dark ? '#94a3b8' : '#64748b';
  const chipBg = dark ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
  const chipBorder = dark ? 'rgba(255,255,255,0.10)' : '#e2e8f0';
  const shadow = dark ? '0 12px 32px rgba(0,0,0,0.45)' : '0 12px 32px rgba(2,6,23,0.10)';

  const logo = data.logo_url || DEFAULT_FORUM_LOGO_URL;

  return (
    <div
      style={{
        fontFamily: 'system-ui, sans-serif',
        padding: compact ? 12 : 14,
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        border: `1px solid ${border}`,
        borderRadius: 16,
        background: bg,
        color: text,
        boxShadow: shadow,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            overflow: 'hidden',
            border: `1px solid ${chipBorder}`,
            background: dark ? 'rgba(255,255,255,0.03)' : '#fff',
            flex: '0 0 auto',
          }}
        >
          <img
            src={logo}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            loading="lazy"
          />
        </div>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: 14,
              lineHeight: '18px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.name}
          </div>
          <div style={{ fontSize: 12, color: muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data.category}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
          <div
            style={{
              padding: '6px 8px',
              borderRadius: 12,
              background: chipBg,
              border: `1px solid ${chipBorder}`,
              textAlign: 'right',
              minWidth: 80,
            }}
            title="Genel sıralama (Skor)"
          >
            <div style={{ fontSize: 10, color: muted, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
              Genel
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: accent, lineHeight: '16px' }}>
              #{data.globalRank ?? '—'}
            </div>
          </div>
          {!compact && (
            <div
              style={{
                padding: '6px 8px',
                borderRadius: 12,
                background: chipBg,
                border: `1px solid ${chipBorder}`,
                textAlign: 'right',
                minWidth: 80,
              }}
              title="Kategori sıralaması (Skor)"
            >
              <div style={{ fontSize: 10, color: muted, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Kategori
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, lineHeight: '16px' }}>
                #{data.categoryRank ?? '—'}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          marginTop: 12,
          display: 'grid',
          gridTemplateColumns: compact ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: 8,
          fontSize: 12,
        }}
      >
        <StatCell label="Skor" value={data.score.toLocaleString('tr-TR')} muted={muted} />
        <StatCell label="Üye" value={data.members.toLocaleString('tr-TR')} muted={muted} />
        <StatCell label="Konu" value={data.topics.toLocaleString('tr-TR')} muted={muted} />
        <StatCell label="İleti" value={data.posts.toLocaleString('tr-TR')} muted={muted} />
      </div>

      <a
        href={`https://kitapcik.net/forum/${forumId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          marginTop: 12,
          fontSize: 12,
          color: accent,
          textDecoration: 'none',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          paddingTop: 10,
          borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : '#eef2f7'}`,
        }}
      >
        <span style={{ opacity: 0.95 }}>{label}</span>
        <span style={{ fontWeight: 700 }}>Detay →</span>
      </a>
    </div>
  );
};

function StatCell({ label, value, muted }: { label: string; value: string; muted: string }) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 12,
        border: '1px solid rgba(148,163,184,0.18)',
        background: 'rgba(148,163,184,0.06)',
      }}
    >
      <div style={{ fontSize: 10, color: muted, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, marginTop: 3 }}>{value}</div>
    </div>
  );
}

export default EmbedForum;
